import type {
  SuggestedPipeline,
  SuggestedPipelineRound,
  BackendInterviewRound,
} from '../api/interviewBuilderApi';
import type {
  CodingConfig,
  GDConfig,
  IndividualConfig,
  InterviewConfig,
  InterviewNodeData,
  InterviewType,
} from '../types/interviewBuilder';
import { getDefaultConfig, getInterviewTypeMeta } from '../types/interviewBuilder';

export interface MappedRoundSlot {
  id: string;
  name: string;
  interview: InterviewNodeData | null;
  scheduledDate?: string;
}

const VALID_INTERVIEW_TYPES: InterviewType[] = ['mock_ai', 'coding', 'group_discussion'];
const FIRST_ROUND_OFFSET_HOURS = 2;
const ROUND_GAP_HOURS = 2;

function formatDateTimeLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function assignScheduledDates(slots: MappedRoundSlot[]): MappedRoundSlot[] {
  const firstDate = new Date();
  firstDate.setHours(firstDate.getHours() + FIRST_ROUND_OFFSET_HOURS);

  let filledIndex = 0;
  return slots.map((slot) => {
    if (!slot.interview) return slot;

    const date = new Date(firstDate);
    date.setHours(date.getHours() + filledIndex * ROUND_GAP_HOURS);
    filledIndex += 1;

    return { ...slot, scheduledDate: formatDateTimeLocal(date) };
  });
}

function isInterviewType(value: string): value is InterviewType {
  return VALID_INTERVIEW_TYPES.includes(value as InterviewType);
}

function toInterviewNode(round: SuggestedPipelineRound): InterviewNodeData {
  const interviewType = isInterviewType(round.interviewType)
    ? round.interviewType
    : 'mock_ai';
  const meta = getInterviewTypeMeta(interviewType);
  const defaultConfig = getDefaultConfig(interviewType);
  const config = {
    ...defaultConfig,
    ...(round.config as Partial<InterviewConfig>),
  } as InterviewConfig;

  return {
    label: round.label || meta.title,
    interviewType,
    config,
    isConfigured: round.isConfigured ?? false,
  };
}

export function mapSuggestedRoundsToSlots(
  rounds: SuggestedPipelineRound[],
  minSlots = 3,
  maxSlots = 7,
): MappedRoundSlot[] {
  const sorted = [...rounds].sort((a, b) => a.order - b.order).slice(0, maxSlots);
  const filledByOrder = new Map(
    sorted.map((round) => [
      round.order,
      {
        id: `slot-${round.order}`,
        name: round.name || `Round ${round.order}`,
        interview: toInterviewNode(round),
      } satisfies MappedRoundSlot,
    ]),
  );

  const slotCount = Math.max(minSlots, sorted.length);
  const result: MappedRoundSlot[] = [];

  for (let order = 1; order <= slotCount; order++) {
    const filled = filledByOrder.get(order);
    if (filled) {
      result.push(filled);
    } else {
      result.push({
        id: `slot-${order}`,
        name: `Round ${order}`,
        interview: null,
      });
    }
  }

  return assignScheduledDates(result);
}

export interface AppliedSuggestedPipeline {
  name: string;
  jobDescription: string;
  startsAt: string;
  deadline: string;
  rounds: MappedRoundSlot[];
}

export function applySuggestedPipeline(
  pipeline: SuggestedPipeline,
  jobDescription?: string | null,
): AppliedSuggestedPipeline {
  const rounds = mapSuggestedRoundsToSlots(pipeline.rounds);
  const startsAt = rounds.find((round) => round.scheduledDate)?.scheduledDate || '';

  return {
    name: pipeline.name,
    jobDescription: jobDescription?.trim() || '',
    startsAt,
    deadline: pipeline.deadline || '',
    rounds,
  };
}

const BUILDER_TO_RUNTIME_TYPE: Record<InterviewType, BackendInterviewRound['type']> = {
  mock_ai: 'aptitude',
  coding: 'coding',
  group_discussion: 'gd',
};

const GD_CATEGORY_TO_TOPIC: Record<string, string> = {
  technology: 'technical',
  current_affairs: 'current-affairs',
  abstract: 'abstract',
};

function mapConfigToBackend(
  interviewType: InterviewType,
  config: InterviewConfig,
): Record<string, unknown> {
  if (interviewType === 'mock_ai') {
    const c = config as IndividualConfig;
    return {
      difficulty: c.difficulty,
      experience_focus: c.experience_focus,
      mode: c.mode,
    };
  }

  if (interviewType === 'coding') {
    const c = config as CodingConfig;
    return {
      language: c.language,
      difficulty: c.difficulty,
      category: c.category ? c.category.replace(/_/g, '-') : 'arrays',
      mode: c.mode,
      ...(c.mode === 'timed' && c.timed_duration_seconds
        ? { timed_duration_seconds: c.timed_duration_seconds }
        : {}),
    };
  }

  const c = config as GDConfig;
  return {
    topicCategory: GD_CATEGORY_TO_TOPIC[c.category] || c.category || 'technical',
    topic_id: c.topic_id,
    topic_title: c.topic_title,
    difficulty: c.difficulty,
    participantCount: 4,
  };
}

export function mapRoundsForBackendSchedule(
  slots: MappedRoundSlot[],
): BackendInterviewRound[] {
  return slots
    .filter((slot) => slot.interview && slot.scheduledDate)
    .map((slot) => ({
      id: slot.id,
      type: BUILDER_TO_RUNTIME_TYPE[slot.interview!.interviewType],
      scheduledDate: slot.scheduledDate!,
      isConfigured: slot.interview!.isConfigured,
      config: mapConfigToBackend(slot.interview!.interviewType, slot.interview!.config),
    }));
}
